import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import { NAV_CATEGORIES, CATEGORY_META } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import { PROFILES } from "@/lib/brand";
import * as Icons from "lucide-react";

export default function ConnectPage() {
  const { lang, t } = useLang();
  const { settings } = useSettings();
  const [params] = useSearchParams();
  useSeo({ title: t("connect_title"), description: t("connect_sub"), lang });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    intent: "", category: params.get("category") || "",
    profile: PROFILES.some((p) => p.key === params.get("profile")) ? params.get("profile") : "",
    needs: [],
    message: params.get("ref") ? `${lang === "fr" ? "Concernant" : "Regarding"} : ${params.get("ref")}\n\n` : "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const profile = PROFILES.find((p) => p.key === form.profile);
  const toggleNeed = (n) => setForm((f) => ({ ...f, needs: f.needs.includes(n) ? f.needs.filter((x) => x !== n) : [...f.needs, n] }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/requests", { ...form, locale: lang });
      setSent(true);
      toast.success(t("form_success"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
      <div className="grid gap-16 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t("nav_connect")}</span>
          <h1 className="mt-3 font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">{t("connect_title")}</h1>
          <p className="mt-5 text-lg leading-relaxed text-anthracite/70">{t("connect_sub")}</p>
          <div className="mt-10 border-l-2 border-champagne pl-5">
            <p className="font-serif text-xl italic text-anthracite/80">
              “{settings.human?.[`title_${lang}`] || t("experts_sub")}”
            </p>
          </div>
          <p className="mt-8 text-sm text-anthracite/50">{settings.contact?.[`note_${lang}`]}</p>
        </div>

        {sent ? (
          <div className="flex flex-col items-center justify-center border border-champagne/20 bg-champagne/5 p-12 text-center" data-testid="connect-success">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-champagne text-2xl text-white">✓</div>
            <h2 className="font-serif text-2xl text-anthracite">{t("form_success")}</h2>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="connect-form" className="space-y-5 border border-tertiary bg-white p-8">
            <div>
              <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-anthracite/50">{lang === "fr" ? "Vous êtes" : "You are"}</span>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="connect-profile-grid">
                {PROFILES.map((p) => {
                  const Icon = Icons[p.icon] || Icons.Circle;
                  const active = form.profile === p.key;
                  return (
                    <button type="button" key={p.key} data-testid={`connect-profile-${p.key}`} onClick={() => setForm((f) => ({ ...f, profile: p.key, needs: [] }))}
                      className={`flex flex-col items-start gap-2 border p-3 text-left text-xs transition-colors duration-300 ${active ? "border-champagne bg-champagne/10 text-anthracite" : "border-tertiary text-anthracite/70 hover:border-champagne/50"}`}>
                      <Icon className={`h-4 w-4 ${active ? "text-champagne" : "text-anthracite/50"}`} strokeWidth={1.5} />
                      <span className="leading-snug">{(lang === "fr" ? p.fr : p.en)[0]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {profile && (
              <div data-testid="connect-needs">
                <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-anthracite/50">{lang === "fr" ? "Vous recherchez" : "You are looking for"}</span>
                <div className="flex flex-wrap gap-2">
                  {profile.needs[lang].map((n) => {
                    const active = form.needs.includes(n);
                    return (
                      <button type="button" key={n} onClick={() => toggleNeed(n)} data-testid={`connect-need-${n}`}
                        className={`rounded-full border px-4 py-1.5 text-xs transition-colors duration-300 ${active ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary text-anthracite/70 hover:border-anthracite"}`}>
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t("form_name")} required>
                <input required data-testid="form-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("form_email")} required>
                <input required type="email" data-testid="form-email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("form_company")}>
                <input data-testid="form-company" value={form.company} onChange={(e) => update("company", e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("form_phone")}>
                <input data-testid="form-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("form_category")}>
                <select data-testid="form-category" value={form.category} onChange={(e) => update("category", e.target.value)} className={inputCls}>
                  <option value="">{t("form_choose")}</option>
                  {NAV_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{CATEGORY_META[c][lang]}</option>
                  ))}
                </select>
              </Field>
              <Field label={t("form_intent")}>
                <select data-testid="form-intent" value={form.intent} onChange={(e) => update("intent", e.target.value)} className={inputCls}>
                  <option value="">{t("form_choose")}</option>
                  <option value="offer">{t("intent_offer")}</option>
                  <option value="seek">{t("intent_seek")}</option>
                  <option value="both">{t("intent_both")}</option>
                </select>
              </Field>
            </div>
            <Field label={t("form_message")} required>
              <textarea required rows={5} data-testid="form-message" value={form.message} onChange={(e) => update("message", e.target.value)} className={inputCls} />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="w-full bg-anthracite py-4 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-black disabled:opacity-50"
            >
              {submitting ? "…" : t("form_submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full border border-tertiary bg-white px-4 py-3 text-sm text-anthracite outline-none transition-colors duration-300 focus:border-champagne focus:ring-1 focus:ring-champagne";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-anthracite/50">
        {label}{required && <span className="text-champagne"> *</span>}
      </span>
      {children}
    </label>
  );
}
